import type { LOG_LEVELS } from './constants.js';
import type { LogContext } from './context.js';

export type LogLevel = (typeof LOG_LEVELS)[number];

/**
 * A single structured log record. Handed to every registered sink after the
 * entry has been written to the console.
 */
export type LogEntry = {
  level: LogLevel;
  scope: string;
  message: string;
  context?: LogContext;
};

/**
 * Receives every log entry. Sinks let apps send logs to external systems (e.g.
 * a Sentry bridge) without the logger taking on a dependency.
 */
export type LogSink = (entry: LogEntry) => void;

// Its own module so it stays a single shared singleton regardless of entry point.
const sinks: LogSink[] = [];

/** Register a sink for all later log entries. Returns a function that unregisters it. */
export function registerLogSink(sink: LogSink): () => void {
  sinks.push(sink);
  return () => {
    const index = sinks.indexOf(sink);
    if (index !== -1) {
      sinks.splice(index, 1);
    }
  };
}

/**
 * Remove all registered sinks. Primarily for tests and teardown.
 *
 * @internal
 */
export function clearLogSinks(): void {
  sinks.length = 0;
  warnedSinks = new WeakSet<LogSink>();
}

let warnedSinks = new WeakSet<LogSink>();

/**
 * Dispatch an entry to every registered sink. A throwing sink must never break
 * application logging, so failures are swallowed. The first failure per sink
 * goes once to console.error, so a wedged sink (bad Sentry DSN, dead network)
 * does not make telemetry go dark silently.
 */
export function emitToSinks(entry: LogEntry): void {
  for (const sink of sinks) {
    try {
      sink(entry);
    } catch (error) {
      if (!warnedSinks.has(sink)) {
        warnedSinks.add(sink);
        console.error('[logger] a log sink threw; further failures from it are silenced', error);
      }
    }
  }
}
